/**
 * The rows of the room's Context (stage 2b): a pinned item, a project file and
 * a project picture. One shape for all three: a 36px box (glyph, spinner or
 * thumbnail), the name, one line of what it is and where it came from, a
 * neutral state tag, and the row's menu. The whole row opens its preview.
 */
import {
  AlertCircle,
  Check,
  ExternalLink,
  Eye,
  FileSpreadsheet,
  FileText,
  Link2,
  Loader2,
  Orbit,
  Pin,
  Play,
  RadioTower,
  RotateCw,
  Target,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";
import { Image } from "@/components/Image";
import { posterFrameUrl } from "@/app/routes/dashboard/content-calendar/videoThumbnails";
import type { WorkshopContextItem } from "@/types/workshop.type";
import { cn } from "@/utils/helpers";
import { useWorkshopFileUrl, type WorkshopFileRow } from "../api/useWorkshopFiles";
import type { WorkshopMediaItem } from "../api/useWorkshopMedia";
import { formatAttribution } from "../attribution";
import { formatMadeDate } from "../workshopOutputs";
import { contextKindWord, fileTypeWord } from "./SourcePreview";
import WorkshopRowMenu, { type RowMenuItem } from "./WorkshopRowMenu";

const MB = 1024 * 1024;

function megabytes(bytes: number): string {
  return (bytes / MB).toFixed(1);
}

function Row({
  unread,
  busy,
  box,
  name,
  line,
  lineTone,
  tag,
  openLabel,
  onOpen,
  menu,
  progress,
}: {
  unread: boolean;
  busy?: boolean;
  box: React.ReactNode;
  name: React.ReactNode;
  line: string;
  lineTone?: "alert";
  tag?: React.ReactNode;
  openLabel: string;
  onOpen?: () => void;
  menu?: React.ReactNode;
  progress?: React.ReactNode;
}) {
  return (
    <li className={cn("ws-crow", unread && "co-idea-row-glow")} aria-busy={busy || undefined}>
      {onOpen ? (
        <button type="button" className="ws-crow-hit" aria-label={openLabel} onClick={onOpen} />
      ) : null}
      <span className="ws-crow-box" aria-hidden="true">
        {box}
      </span>
      <span className="ws-crow-text">
        <span className="ws-crow-name">{name}</span>
        <span className="ws-crow-line" data-tone={lineTone}>
          {line}
        </span>
        {progress}
      </span>
      {tag ? <span className="ws-crow-tag">{tag}</span> : null}
      {menu ? <span className="ws-crow-actions">{menu}</span> : null}
    </li>
  );
}

export function PinnedRow({
  item,
  viewerId,
  unread,
  canWrite,
  isPhone,
  onOpen,
  onUnpin,
}: {
  item: WorkshopContextItem;
  viewerId: string | null;
  unread: boolean;
  canWrite: boolean;
  isPhone: boolean;
  onOpen: () => void;
  onUnpin: () => void;
}) {
  const by = formatAttribution(item.attribution?.createdBy, viewerId, { includeContext: false });
  const line = [
    contextKindWord(item.kind),
    `pinned ${formatMadeDate(item.createdAt)}${by ? ` ${by}` : ""}`,
  ].join(" · ");
  const items: RowMenuItem[] = [
    { id: "open", label: "Preview", icon: Eye },
    ...(item.kind === "url" && item.url ? [{ id: "link", label: "Open the link", icon: ExternalLink }] : []),
    ...(canWrite ? [{ id: "unpin", label: "Unpin", icon: Trash2, danger: true }] : []),
  ];
  const Glyph = PIN_GLYPHS[item.kind] ?? Pin;
  // One line: the kind's mark, the label, the kind in mono; the rest in its title.
  return (
    <li
      className={cn("ws-pin-row", unread && "co-idea-row-glow")}
      title={item.note ? `${item.note} · ${line}` : line}
    >
      <button type="button" className="ws-crow-hit" aria-label={`Preview ${item.label}`} onClick={onOpen} />
      <Glyph className="ws-pin-glyph" aria-hidden="true" />
      <span className="ws-pin-title">{item.label}</span>
      <span className="ws-pin-kind">{PIN_KIND_WORD[item.kind] ?? contextKindWord(item.kind).toUpperCase()}</span>
      <span className="ws-crow-actions">
        <WorkshopRowMenu
          label={`Actions for ${item.label}`}
          title={item.label}
          meta={contextKindWord(item.kind)}
          items={items}
          asSheet={isPhone}
          onAction={(id) => {
            if (id === "open") onOpen();
            else if (id === "link" && item.url) window.open(item.url, "_blank", "noopener,noreferrer");
            else if (id === "unpin") onUnpin();
          }}
        />
      </span>
    </li>
  );
}

/** The mark each kind of pin carries. */
const PIN_GLYPHS: Record<string, typeof Pin> = {
  universe_fact: Orbit,
  strategy: Target,
  market_signal: RadioTower,
  trend: TrendingUp,
  document: FileText,
  url: Link2,
};

const PIN_KIND_WORD: Record<string, string> = {
  universe_fact: "UNIVERSE",
  strategy: "STRATEGY",
  market_signal: "SIGNAL",
  trend: "TREND",
  document: "FILE",
  url: "LINK",
};

/** A frame from the stored video. The play mark stays until that frame is painted. */
function VideoThumb({ projectId, fileId }: { projectId: string; fileId: string }) {
  const query = useWorkshopFileUrl(projectId, fileId);
  const [painted, setPainted] = useState(false);
  const [dead, setDead] = useState(false);
  const url = query.data?.url;
  if (!url || dead) return <Play className="size-4" strokeWidth={1.7} />;
  return (
    <>
      <video
        src={posterFrameUrl(url)}
        muted
        playsInline
        preload="metadata"
        className="ws-crow-thumb"
        onLoadedData={() => setPainted(true)}
        onError={() => setDead(true)}
      />
      {painted ? null : <Play className="size-4" strokeWidth={1.7} />}
    </>
  );
}

export function FileRow({
  file,
  projectId,
  viewerId,
  unread,
  canWrite,
  isPhone,
  onOpen,
  onRetry,
  onRemove,
}: {
  file: WorkshopFileRow;
  projectId: string;
  viewerId: string | null;
  unread: boolean;
  canWrite: boolean;
  isPhone: boolean;
  onOpen: () => void;
  onRetry: () => void;
  onRemove: () => void;
}) {
  const type = fileTypeWord(file.type);
  const name = file.fileName ?? type;
  // A room upload still in flight: nothing to open, remove or retry yet.
  const placeholder = file.id.startsWith("pending-");
  const uploading = file.state === "uploading";
  const reading = file.state === "processing";
  const failed = file.state === "failed";
  const by = formatAttribution(file.attribution?.createdBy, viewerId, { includeContext: false });
  const thread = file.attribution?.createdBy.thread;
  const inThread = thread?.state === "own" && thread.title ? ` in ${thread.title}` : "";

  // Numbers only once the upload has reported bytes reaching storage; before
  // that (and for a converted spreadsheet) it honestly says "uploading".
  const sent = file.upload && file.upload.sent > 0 && file.upload.total > 0 ? file.upload : null;
  let line: string;
  if (uploading) {
    line = sent
      ? `${type} · uploading, ${megabytes(sent.sent)} of ${megabytes(sent.total)} MB`
      : `${type} · uploading`;
  } else if (reading) {
    line = `${type} · reading it for the chat`;
  } else if (failed) {
    line = file.error ? `${type} · ${file.error}` : `${type} · couldn't be read`;
  } else if (file.sourceUrl) {
    line = `${type} · attached from a link${inThread} · ${formatMadeDate(file.createdAt)}`;
  } else {
    line = `${type} · added ${formatMadeDate(file.createdAt)}${by ? ` ${by}` : ""}`;
  }

  const Icon = file.type === "spreadsheet" ? FileSpreadsheet : FileText;
  const showVideo = file.type === "video" && !placeholder && !uploading && !failed;
  const box =
    uploading || reading ? (
      <Loader2 className="ws-crow-spin size-4 animate-spin" strokeWidth={1.8} />
    ) : failed ? (
      <AlertCircle className="size-4 text-co-alert-ink" strokeWidth={1.8} />
    ) : showVideo ? (
      <VideoThumb projectId={projectId} fileId={file.id} />
    ) : (
      <Icon className="size-4" strokeWidth={1.7} />
    );

  const fraction = sent ? sent.sent / sent.total : null;
  const progress = uploading ? (
    <span
      className="ws-crow-progress"
      role="progressbar"
      aria-label={`Uploading ${name}`}
      aria-valuemin={fraction === null ? undefined : 0}
      aria-valuemax={fraction === null ? undefined : 100}
      aria-valuenow={fraction === null ? undefined : Math.round(fraction * 100)}
      data-indeterminate={fraction === null || undefined}
    >
      <span style={fraction === null ? undefined : { transform: `scaleX(${fraction})` }} />
    </span>
  ) : null;

  const items: RowMenuItem[] = [
    { id: "open", label: "Preview", icon: Eye },
    ...(canWrite && failed && file.canRetry ? [{ id: "retry", label: "Try reading it again", icon: RotateCw }] : []),
    ...(canWrite ? [{ id: "remove", label: "Remove from project", icon: Trash2, danger: true }] : []),
  ];

  return (
    <Row
      unread={unread}
      busy={uploading || reading}
      box={box}
      name={name}
      line={line}
      lineTone={failed ? "alert" : undefined}
      progress={progress}
      tag={
        file.state === "completed" ? (
          <span className="ws-tag ws-tag--read">
            <Check className="size-3" strokeWidth={2} aria-hidden="true" />
            Read by the chat
          </span>
        ) : null
      }
      openLabel={`Preview ${name}`}
      onOpen={placeholder || uploading ? undefined : onOpen}
      menu={
        placeholder ? null : (
          <WorkshopRowMenu
            label={`Actions for ${name}`}
            title={name}
            meta={type}
            items={items}
            asSheet={isPhone}
            onAction={(id) => {
              if (id === "open") onOpen();
              else if (id === "retry") onRetry();
              else if (id === "remove") onRemove();
            }}
          />
        )
      }
    />
  );
}

export function PictureRow({
  item,
  viewerId,
  unread,
  isPhone,
  items,
  onOpen,
  onAction,
}: {
  item: WorkshopMediaItem;
  viewerId: string | null;
  unread: boolean;
  isPhone: boolean;
  /** The menu (usePictureActions.menuItems). */
  items: RowMenuItem[];
  onOpen: () => void;
  onAction: (id: string) => void;
}) {
  const by = formatAttribution(item.attribution?.createdBy, viewerId, { includeContext: false });
  const kind = item.isAiGenerated
    ? "Generated picture"
    : item.copiedFromBrandMediaId || item.library?.relation === "copied_from"
      ? "Picture from the brand library"
      : "Picture";
  const line = item.pending
    ? `${kind} · adding`
    : `${kind} · added ${formatMadeDate(item.linkedAt)}${by ? ` ${by}` : ""}`;
  const src = item.thumbUrl ?? item.url;
  return (
    <Row
      unread={unread}
      busy={item.pending}
      box={
        <>
          {src ? <Image src={src} alt="" className="ws-crow-thumb" /> : null}
          {item.pending ? <Loader2 className="ws-crow-spin size-4 animate-spin" strokeWidth={1.8} /> : null}
        </>
      }
      name={item.fileName}
      line={line}
      tag={item.library ? <span className="ws-tag">In the library</span> : null}
      openLabel={`Preview ${item.fileName}`}
      onOpen={item.pending ? undefined : onOpen}
      menu={
        item.pending ? null : (
          <WorkshopRowMenu
            label={`Actions for ${item.fileName}`}
            title={item.fileName}
            meta={kind}
            items={items}
            asSheet={isPhone}
            onAction={(id) => (id === "open" ? onOpen() : onAction(id))}
          />
        )
      }
    />
  );
}
